import { toGenericPriceKey, toOracleKey } from '../../assets'
import { GenericTokenList } from '../../types'
import { apyToAprPercent, parseRawAmount } from '../../utils/parsing'
import {
  fetchUpshiftApiVaults,
  hasUpshiftVaults,
  type UpshiftApiVault,
} from './api'
import { UpshiftVault, UpshiftVaults } from './types'

const num = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const isEvmAddress = (a: string): boolean => /^0x[0-9a-fA-F]{40}$/.test(a)

const str = (v: unknown): string | undefined => {
  const s = typeof v === 'string' ? v.trim() : ''
  return s ? s : undefined
}

/**
 * `maxSupply` carries a `1e12` sentinel on every vault whose real cap lives in
 * `depositCap`. Treating it as a number would report a trillion-unit headroom
 * as though it were a measured cap.
 */
const UNCAPPED_SENTINEL = 1e12

/**
 * Room left for new deposits, raw asset-scaled — see
 * `UpshiftVault.depositCapacity`.
 *
 * Returns `undefined` (uncapped) rather than a guess whenever the cap cannot
 * be read as a number: an over-stated capacity invites a deposit that reverts,
 * and a fabricated `'0'` hides a vault that is genuinely open.
 */
const deriveDepositCapacity = (
  v: UpshiftApiVault,
  totalAssetsRaw: string,
): string | undefined => {
  const cap = v.depositCap ?? v.maxSupply
  const normalized = Number(cap?.normalized)
  if (!cap?.raw || !Number.isFinite(normalized)) return undefined
  if (normalized >= UNCAPPED_SENTINEL) return undefined
  try {
    const remaining = BigInt(cap.raw) - BigInt(totalAssetsRaw)
    return remaining > 0n ? remaining.toString() : '0'
  } catch {
    return undefined
  }
}

/** Raw price-per-share, asset-decimal scaled:
 *  `totalAssets * 10**shareDecimals / totalSupply`. `'0'` for an empty
 *  vault (no supply). */
const derivePricePerShare = (
  totalAssetsRaw: string,
  totalSupplyRaw: string,
  shareDecimals: number,
): string => {
  try {
    const supply = BigInt(totalSupplyRaw)
    if (supply === 0n) return '0'
    const assets = BigInt(totalAssetsRaw)
    return ((assets * 10n ** BigInt(shareDecimals)) / supply).toString()
  } catch {
    return '0'
  }
}

function parseVault(
  v: UpshiftApiVault,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
): UpshiftVault | null {
  // Receipt token is what a depositor holds — key the vault by it so the
  // user-balance lookup resolves correctly. Falls back to the deposit
  // contract on older single-token vaults.
  const receiptAddr = (v?.receipt?.address ?? v?.address ?? '').toLowerCase()
  const vaultAddr = (v?.address ?? receiptAddr).toLowerCase()
  const asset0 = v.depositAssets?.find((a) => a?.address)
  const assetAddr = (asset0?.address ?? '').toLowerCase()
  // Every accepted leg, not just the first — the deposit call names the asset
  // it pulls, so the others are real entry paths and not decoration.
  const depositAssets = (v.depositAssets ?? [])
    .map((a) => (a?.address ?? '').toLowerCase())
    .filter((a) => isEvmAddress(a))
  if (!receiptAddr || !assetAddr) return null
  // Skip non-EVM rows (Stellar/Solana addresses) that share the listing.
  if (!isEvmAddress(receiptAddr) || !isEvmAddress(assetAddr)) return null

  const assetMeta = tokenList[assetAddr]
  const decimals = Number(v.receipt?.decimals ?? v.decimals ?? 18)
  const assetDecimals = Number(asset0?.decimals ?? assetMeta?.decimals ?? 18)

  const totalAssetsRaw = v.totalAssets?.raw?.toString() ?? '0'
  const totalSupplyRaw = v.totalSupply?.raw?.toString() ?? '0'
  const totalAssetsFormatted = Number(
    parseRawAmount(totalAssetsRaw, assetDecimals),
  )

  // The Upshift API reports APY (percent). Our rate fields are APR (percent),
  // so convert — matching the intrinsic yield fetcher (`upshiftFetcher`).
  const base = num(v.apy?.apy) ?? 0
  const campaign = num(v.apy?.campaignApy)
  const supplyRate = apyToAprPercent(base)
  const rewardsRate = campaign != null ? apyToAprPercent(campaign) : 0

  // Underlying USD: prefer our price map (oracle-keyed), fall back to the
  // API's own implied valuation (latest_reported_tvl / totalAssets).
  const oracleKey =
    toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(assetAddr, chainId)
  const apiTvlUsd = num(v.latest_reported_tvl) ?? 0
  const impliedPriceUsd =
    totalAssetsFormatted > 0 ? apiTvlUsd / totalAssetsFormatted : 0
  const priceUsd = prices[oracleKey] ?? prices[assetAddr] ?? impliedPriceUsd

  const totalAssetsUsd = apiTvlUsd || totalAssetsFormatted * priceUsd

  const name = (v.name ?? '').trim()
  const symbol = (v.receipt?.symbol ?? '').trim()

  // The curator. `strategists[0].name` is the only usable handle — every
  // vault in the listing reports `address: "0x"`.
  const strategist = (v.strategists ?? []).find((s) => str(s?.name))
  const curatorName = str(strategist?.name)

  // Effective fees: a waived fee is 0 TODAY, and the standing schedule is kept
  // beside it because the waiver is revocable. Reading the bare rate would
  // over-state five vaults; ignoring the schedule would under-state them the
  // moment a curator turns it back on.
  const performanceStanding = num(v.fees?.performance) ?? 0
  const managementStanding = num(v.fees?.management) ?? 0
  const performanceWaived = v.fees?.isPerformanceWaived === true
  const managementWaived = v.fees?.isManagementWaived === true

  // The instant leg exists on one vault today; everywhere else the exit is the
  // provider default (`request-based`), so no mode is declared.
  const instant = v.instant_redeem_config
  const instantRedeemEnabled = !!instant && instant.isPaused !== true
  const spreadBps = instantRedeemEnabled
    ? Math.max(
        0,
        ...(instant?.redeemableAssets ?? []).map((a) => num(a?.spreadBps) ?? 0),
      )
    : undefined

  return {
    address: receiptAddr,
    vaultAddress: vaultAddr,
    underlying: assetAddr,
    depositAssets,
    symbol,
    name: name || symbol,
    decimals,
    assetDecimals,
    totalAssets: totalAssetsRaw,
    totalSupply: totalSupplyRaw,
    pricePerShare: derivePricePerShare(
      totalAssetsRaw,
      totalSupplyRaw,
      decimals,
    ),
    supplyRate,
    rewardsRate,
    depositRate: supplyRate + rewardsRate,
    apy: {
      base: num(v.apy?.apy),
      campaign,
      points: num(v.apy?.pointsApy),
      underlying: num(v.apy?.underlyingApy),
    },
    curatorName,
    curatorLogoURI: str(strategist?.logo),
    description: str(v.description),
    version: str(v.version),

    fee: performanceWaived ? 0 : performanceStanding,
    managementFee: managementWaived ? 0 : managementStanding,
    feeDetail: {
      performanceStanding,
      managementStanding,
      performanceWaived,
      managementWaived,
      performanceWaivedUntilDate: str(v.fees?.performanceFeeWaivedUntilDate),
      managementWaivedUntilDate: str(v.fees?.managementFeeWaivedUntilDate),
    },

    withdrawalCooldownSeconds: num(v.lagDuration) ?? 0,
    ...(instantRedeemEnabled
      ? {
          withdrawalMode: 'fee-or-queued' as const,
          withdrawFeeBps: spreadBps,
          instantRedeemEnabled: true,
        }
      : {}),

    isDepositPaused: v.isDepositPaused ?? false,
    isWithdrawalPaused: v.isWithdrawalPaused ?? false,
    isClosed: v.withdrawalOnly === true,
    depositCapacity: deriveDepositCapacity(v, totalAssetsRaw),
    asset: assetMeta,
    priceUsd,
    totalAssetsFormatted,
    totalAssetsUsd,
  }
}

/**
 * Is this listing row ours to serve?
 *
 * `isVisible: false` is Upshift hiding a vault from THEIR app, and it covers
 * two unrelated things: test vaults (over twenty of them, at zero TVL), and
 * vaults in WIND-DOWN that real depositors are still in — Hyperbeat Ultra uBTC
 * ($878k) and Lombard LBTC among them. Dropping both took the curator, the
 * live rate and the live TVL off the wind-down rows, and the listing kept
 * serving whatever the recorder last stored for them: a row branded plainly
 * "Upshift", with no curator and frozen numbers, that a holder still needs an
 * exit from.
 *
 * So a hidden vault is served only when it says it is winding down
 * (`withdrawalOnly`), which is the same field that makes it `isClosed` — the
 * row publishes `canDeposit: false` and an exit, rather than disappearing.
 */
const isServable = (v: UpshiftApiVault): boolean =>
  v?.isVisible !== false || v?.withdrawalOnly === true

/**
 * One-shot fetcher for the Upshift vaults on a chain (see
 * {@link isServable} for which of them we serve).
 *
 * HTTP-only (Upshift public REST listing) — no multicall executor needed,
 * mirroring the Lagoon fetcher. The endpoint is global; we filter to the
 * requested chain. Returns an empty map for chains without an Upshift
 * deployment.
 *
 * @param chainId    target chain
 * @param prices     optional price map keyed by oracle key / underlying
 * @param tokenList  optional token list for hydrating `asset` metadata
 *
 * @returns `UpshiftVaults` — map keyed by lowercased receipt-token address.
 */
export const fetchUpshiftVaults = async (
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): Promise<UpshiftVaults> => {
  if (!hasUpshiftVaults(chainId)) return {}

  const items = await fetchUpshiftApiVaults()
  const want = Number(chainId)
  const out: UpshiftVaults = {}
  for (const item of items) {
    if (Number(item?.chainId) !== want) continue
    if (!isServable(item)) continue
    const parsed = parseVault(item, chainId, prices, tokenList)
    if (parsed) out[parsed.address] = parsed
  }
  return out
}
